/**
 * PortalLearning — Revenue optimization insights from scoring_results + outcomes
 *
 * Shows which user profiles convert best on which decision paths,
 * enabling data-driven rule tuning. This is the "learning layer"
 * that precedes ML — rules-based pattern discovery.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import { Brain, TrendingUp, Loader2, Lightbulb, DollarSign } from 'lucide-react';
import { supabaseGet } from '@/utils/supabaseRest';

interface ScoringResult {
  session_id: string;
  intent_score: number;
  monetization_score: number;
  decision: string;
  intent_breakdown: Record<string, number>;
  monetization_breakdown: Record<string, number>;
  vertical_id: string | null;
  computed_at: string;
}

interface Outcome {
  session_id: string;
  decision: string;
  vertical_id: string | null;
  revenue: number;
  outcome_status: string;
}

interface Insight {
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  action: string;
}

const COLORS = { CALL: '#10b981', LEAD: '#3b82f6', MULTI_OFFER: '#8b5cf6' };

export default function PortalLearning() {
  const [results, setResults] = useState<ScoringResult[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o] = await Promise.all([
        supabaseGet('scoring_results?order=computed_at.desc&limit=1000'),
        supabaseGet('outcomes?order=created_at.desc&limit=1000'),
      ]);
      setResults(r || []);
      setOutcomes(o || []);
    } catch (err) {
      console.error('Failed to load learning data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Join scoring_results with outcomes by session_id
  const joined = useMemo(() => {
    const outcomeMap = new Map<string, Outcome>();
    outcomes.forEach(o => outcomeMap.set(o.session_id, o));
    return results.map(r => ({
      ...r,
      outcome: outcomeMap.get(r.session_id),
    }));
  }, [results, outcomes]);

  // Revenue by decision type
  const revenueByDecision = useMemo(() => {
    const map: Record<string, { total: number; count: number; converted: number }> = {};
    joined.forEach(j => {
      if (!map[j.decision]) map[j.decision] = { total: 0, count: 0, converted: 0 };
      map[j.decision].count++;
      if (j.outcome?.outcome_status === 'converted') {
        map[j.decision].total += Number(j.outcome.revenue || 0);
        map[j.decision].converted++;
      }
    });
    return Object.entries(map).map(([decision, data]) => ({
      decision,
      avg_revenue: data.converted > 0 ? Math.round(data.total / data.converted * 100) / 100 : 0,
      conversion_rate: data.count > 0 ? Math.round(data.converted / data.count * 1000) / 10 : 0,
      total_revenue: Math.round(data.total * 100) / 100,
      volume: data.count,
    }));
  }, [joined]);

  // Top converting intent score ranges
  const intentBuckets = useMemo(() => {
    const buckets: Record<string, { count: number; converted: number; revenue: number }> = {
      '0-19': { count: 0, converted: 0, revenue: 0 },
      '20-39': { count: 0, converted: 0, revenue: 0 },
      '40-59': { count: 0, converted: 0, revenue: 0 },
      '60-79': { count: 0, converted: 0, revenue: 0 },
      '80-100': { count: 0, converted: 0, revenue: 0 },
    };
    joined.forEach(j => {
      const score = Number(j.intent_score);
      const key = score < 20 ? '0-19' : score < 40 ? '20-39' : score < 60 ? '40-59' : score < 80 ? '60-79' : '80-100';
      buckets[key].count++;
      if (j.outcome?.outcome_status === 'converted') {
        buckets[key].converted++;
        buckets[key].revenue += Number(j.outcome.revenue || 0);
      }
    });
    return Object.entries(buckets).map(([range, data]) => ({
      range,
      conversion_rate: data.count > 0 ? Math.round(data.converted / data.count * 1000) / 10 : 0,
      avg_revenue: data.converted > 0 ? Math.round(data.revenue / data.converted * 100) / 100 : 0,
      volume: data.count,
    }));
  }, [joined]);

  // Top scoring rules by frequency
  const ruleFrequency = useMemo(() => {
    const freq: Record<string, { count: number; total_points: number }> = {};
    results.forEach(r => {
      const allRules = { ...r.intent_breakdown, ...r.monetization_breakdown };
      Object.entries(allRules).forEach(([rule, points]) => {
        if (!freq[rule]) freq[rule] = { count: 0, total_points: 0 };
        freq[rule].count++;
        freq[rule].total_points += Number(points);
      });
    });
    return Object.entries(freq)
      .map(([rule, data]) => ({ rule: rule.replace(/_/g, ' '), count: data.count, avg_points: Math.round(data.total_points / data.count * 10) / 10 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [results]);

  // Auto-generate insights
  const insights = useMemo((): Insight[] => {
    const ins: Insight[] = [];
    if (joined.length < 5) {
      ins.push({
        title: 'Need more data',
        description: 'Less than 5 scoring decisions recorded. Insights will appear as traffic flows through the engine.',
        impact: 'low',
        action: 'Drive traffic to the widget to generate scoring data.',
      });
      return ins;
    }

    // Check if CALL has higher conversion than LEAD
    const callData = revenueByDecision.find(r => r.decision === 'CALL');
    const leadData = revenueByDecision.find(r => r.decision === 'LEAD');
    if (callData && leadData && callData.conversion_rate > leadData.conversion_rate * 1.5) {
      ins.push({
        title: 'CALL outperforms LEAD significantly',
        description: `CALL converts at ${callData.conversion_rate}% vs LEAD at ${leadData.conversion_rate}%. Consider lowering CALL thresholds to route more users to phone.`,
        impact: 'high',
        action: 'Go to Decision Rules → lower intent_min for CALL from 70 to 60.',
      });
    }

    // Check if high-monetization users are getting MULTI_OFFER
    const highMonetMulti = joined.filter(j => Number(j.monetization_score) >= 60 && j.decision === 'MULTI_OFFER');
    if (highMonetMulti.length > 0) {
      ins.push({
        title: 'High-value users hitting MULTI_OFFER',
        description: `${highMonetMulti.length} sessions with monetization ≥60 ended in MULTI_OFFER instead of CALL or LEAD. These users are valuable but low-intent.`,
        impact: 'medium',
        action: 'Consider adding a re-engagement trigger or adjusting intent scoring rules.',
      });
    }

    // Check for rule imbalance
    const topRule = ruleFrequency[0];
    if (topRule && topRule.count > results.length * 0.8) {
      ins.push({
        title: `"${topRule.rule}" fires on ${Math.round(topRule.count / results.length * 100)}% of sessions`,
        description: 'This rule dominates scoring. If it awards too many points, it may mask the signal from other rules.',
        impact: 'medium',
        action: 'Review this rule\'s points in Scoring Rules. Consider reducing if it\'s always-on.',
      });
    }

    if (ins.length === 0) {
      ins.push({
        title: 'System performing normally',
        description: 'No anomalies detected in current scoring patterns.',
        impact: 'low',
        action: 'Continue monitoring as traffic volume grows.',
      });
    }

    return ins;
  }, [joined, revenueByDecision, ruleFrequency, results.length]);

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Brain className="h-6 w-6 text-emerald-400" />
            Learning & Optimization
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Data-driven insights from scoring decisions and revenue outcomes. Use these to tune your rules.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Insights cards */}
            <div className="space-y-3">
              <h2 className="text-sm font-medium text-slate-300 flex items-center gap-1.5">
                <Lightbulb className="h-4 w-4 text-amber-400" /> Auto-Generated Insights
              </h2>
              {insights.map((insight, i) => (
                <Card key={i} className={`border ${
                  insight.impact === 'high' ? 'border-amber-500/30 bg-amber-500/5' :
                  insight.impact === 'medium' ? 'border-blue-500/30 bg-blue-500/5' :
                  'border-slate-700 bg-slate-800/50'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                        insight.impact === 'high' ? 'bg-amber-400' :
                        insight.impact === 'medium' ? 'bg-blue-400' : 'bg-slate-500'
                      }`} />
                      <div>
                        <div className="text-sm font-medium text-white">{insight.title}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{insight.description}</div>
                        <div className="text-xs text-emerald-400 mt-1">→ {insight.action}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Revenue by decision */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-300">Revenue by Decision Path</CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueByDecision.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={revenueByDecision}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="decision" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                        <Bar dataKey="total_revenue" name="Total Revenue ($)" radius={[4, 4, 0, 0]}>
                          {revenueByDecision.map((entry) => (
                            <Cell key={entry.decision} fill={COLORS[entry.decision as keyof typeof COLORS] || '#6b7280'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-sm text-slate-500">No data yet</div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-300">Conversion by Intent Score Range</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={intentBuckets}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="range" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} unit="%" />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                      <Bar dataKey="conversion_rate" name="CVR %" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Top scoring rules */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300">Most Frequently Triggered Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ruleFrequency.map((rule, i) => (
                    <div key={rule.rule} className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 w-5">{i + 1}.</span>
                      <div className="flex-1">
                        <div className="h-5 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500/40 rounded-full flex items-center px-2"
                            style={{ width: `${Math.min(100, (rule.count / (results.length || 1)) * 100)}%` }}
                          >
                            <span className="text-[10px] text-white truncate">{rule.rule}</span>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-slate-400 w-16 text-right">{rule.count}x</span>
                      <span className={`text-xs font-mono w-12 text-right ${rule.avg_points > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {rule.avg_points > 0 ? '+' : ''}{rule.avg_points}
                      </span>
                    </div>
                  ))}
                  {ruleFrequency.length === 0 && (
                    <div className="text-center py-4 text-sm text-slate-500">No scoring data yet</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Decision performance table */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300">Decision Path Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-700">
                      <th className="text-left py-2">Path</th>
                      <th className="text-right py-2">Volume</th>
                      <th className="text-right py-2">CVR</th>
                      <th className="text-right py-2">Avg Rev</th>
                      <th className="text-right py-2">Total Rev</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueByDecision.map(row => (
                      <tr key={row.decision} className="border-b border-slate-800">
                        <td className="py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            row.decision === 'CALL' ? 'bg-emerald-500/20 text-emerald-400' :
                            row.decision === 'LEAD' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-violet-500/20 text-violet-400'
                          }`}>{row.decision}</span>
                        </td>
                        <td className="py-2 text-right text-slate-300">{row.volume}</td>
                        <td className="py-2 text-right text-slate-300">{row.conversion_rate}%</td>
                        <td className="py-2 text-right text-slate-300">${row.avg_revenue}</td>
                        <td className="py-2 text-right font-medium text-white">${row.total_revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
